import React, { useState, useEffect, useMemo } from "react"
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Stack,
  Button,
  Tooltip,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart"
import { useTranslation } from "react-i18next"
import { GET_PRODUCTS } from "helpers/api_urls"
import config from "config/app.config"
import BoxDiagram from "../BoxDiagram"
import useStoreTabCategoryMap from "./useStoreTabCategoryMap"

const parseCategories = (cat) => {
  if (!cat) return []
  try {
    const parsed = JSON.parse(cat)
    return Array.isArray(parsed) ? parsed : [cat]
  } catch {
    return [cat]
  }
}

const getFirstPhoto = (photosJson) => {
  if (!photosJson) return null
  try {
    const photos = JSON.parse(photosJson)
    if (Array.isArray(photos) && photos.length > 0) return photos[0]
  } catch { /* ignore */ }
  return null
}

/**
 * ProductGrid — receives all filters as resolved props from AddBoxDialog.
 *
 * Props:
 *   selectedPosition  "Wall" | "Base" | "Tall"
 *   priceGroup        Model code (e.g. "1M", "2P")
 *   swatch1           Selected color swatch { label, swatchUrl }
 *   swatch2           (unused, kept for compat)
 *   doorModel         Selected door-model swatch { label, swatchUrl } (optional)
 *   customRalCode     Optional RAL code for lacquer categories (+€180 flat per unique code)
 *   kasoma             Frame type
 *   pomolo             Handle type (empty for Wall)
 *   onAddProduct       Callback
 */
const ProductGrid = ({ selectedPosition, priceGroup, swatch1, swatch2, doorModel, customRalCode, onAddProduct, kasoma, pomolo, productMode, surcharges }) => {
  const { t } = useTranslation()
  const storeTab = useStoreTabCategoryMap()
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [selectedProductName, setSelectedProductName] = useState(null)
  const [filterW, setFilterW] = useState("")
  const [filterD, setFilterD] = useState("")
  const [filterH, setFilterH] = useState("")

  // Custom size state
  const [customWEnabled, setCustomWEnabled] = useState(false)
  const [customDEnabled, setCustomDEnabled] = useState(false)
  const [customHEnabled, setCustomHEnabled] = useState(false)
  const [customW, setCustomW] = useState("")
  const [customD, setCustomD] = useState("")
  const [customH, setCustomH] = useState("")

  // Meter / CubicM unit input state
  const [meterInput, setMeterInput] = useState("")
  const [cubicL, setCubicL] = useState("")
  const [cubicW, setCubicW] = useState("")
  const [cubicH, setCubicH] = useState("")

  // Door options state
  const [doorOptions, setDoorOptions] = useState([])
  const [selectedDoor, setSelectedDoor] = useState(null)
  const [doorOptionsCache, setDoorOptionsCache] = useState(null) // cached all Doors category options

  // Accessories attribute picker (Χαρακτηριστικό): one selection per varying axis.
  // Per customer (May 2026), accessories can have >1 attribute (Watt, color, material).
  // Reuses variants JSON as the source of truth — no schema change.
  const [attributeFilters, setAttributeFilters] = useState({})

  // Dimensions panel collapse
  const [dimsExpanded, setDimsExpanded] = useState(false)

  // Hinge state (for can_hinged products)
  const [hingedEnabled, setHingedEnabled] = useState(false)
  const [hingeCm, setHingeCm] = useState("")
  const [modelAtr1, setModelAtr1] = useState(null) // atr1 from Model category option
  const [modelOptionsCache, setModelOptionsCache] = useState(null)

  // Quantity for a single Add-to-Cart action (Special/Cladding/Pagkos tabs).
  // Cabinet + unit-billed products don't use it — their qty comes from the cart.
  const [addQty, setAddQty] = useState(1)

  const mode = productMode || "Cabinet"
  const isCabinetMode = mode === "Cabinet"
  const isSpecialMode = mode === "Special"
  const isPagkosMode = mode === "Pagkos"
  const isDoorCladdingMode = mode === "DoorCladding"

  // Unit detection for selected product.
  // Worktops: 90cm-depth variants bill per piece (not per meter) — detected by SKU
  // suffix `...090` per customer convention (e.g. QCC.WFS040090 vs QCC.WFS040080).
  // The DB doesn't carry this distinction in any field — applied client-side here.
  const selectedSku = selectedProduct?.sku || ""
  const isPagkos90cmPiece = isPagkosMode && /0?90$/.test(selectedSku)
  const rawUnit = selectedProduct?.unit || "Item"
  const selectedUnit = isPagkos90cmPiece ? "Item" : rawUnit
  const isMeterUnit = selectedUnit === "Meter"
  const isCubicUnit = selectedUnit === "CubicM"
  const isUnitProduct = isMeterUnit || isCubicUnit
  // Show a τεμάχια stepper for Cladding / Special / Pagkos (customer request).
  // Unit-billed products (meter, m³) already carry quantity via their input.
  const showAddQty = (isSpecialMode || isPagkosMode || isDoorCladdingMode) && !isUnitProduct

  // Custom size surcharge pricing per position (from settings)
  const posKey = selectedPosition === "Tall" ? "tall" : selectedPosition === "Wall" ? "wall" : "base"
  const s = surcharges || {}
  const CUSTOM_SURCHARGE = {
    W: s[`${posKey}_W`] ?? 85,
    D: s[`${posKey}_D`] ?? 115,
    H: s[`${posKey}_H`] ?? 115,
  }

  // Calculate total surcharge from custom dimensions.
  // Door cladding (Πόρτες Επένδυσης): customer confirmed special dims NEVER charge extra
  // — whether billed per item or per m² — because they're cut to order, not stocked.
  const customSurcharge = useMemo(() => {
    if (isDoorCladdingMode) return 0
    let surcharge = 0
    if (customWEnabled && customW && filterW) {
      const std = parseInt(filterW)
      const cst = parseInt(customW)
      if (cst > 0 && cst < std) surcharge += CUSTOM_SURCHARGE.W
    }
    if (customDEnabled && customD && filterD) {
      const std = parseInt(filterD)
      const cst = parseInt(customD)
      if (cst > 0 && cst < std) surcharge += CUSTOM_SURCHARGE.D
    }
    if (customHEnabled && customH && filterH) {
      const std = parseInt(filterH)
      const cst = parseInt(customH)
      if (cst > 0 && cst < std) surcharge += CUSTOM_SURCHARGE.H
    }
    return surcharge
  }, [isDoorCladdingMode, customWEnabled, customDEnabled, customHEnabled, customW, customD, customH, filterW, filterD, filterH, CUSTOM_SURCHARGE.W, CUSTOM_SURCHARGE.D, CUSTOM_SURCHARGE.H])

  // Fetch products when filters change
  // For non-cabinet modes, priceGroup is filtered client-side — no need to refetch
  const cabinetPriceGroup = isCabinetMode ? priceGroup : ""
  useEffect(() => {
    // Cabinets require kasoma + pomolo + model; accessories/special do not
    if (isCabinetMode && (!kasoma || !pomolo || !priceGroup)) {
      setProducts([])
      return
    }
    // Non-cabinet modes: fetch immediately. When the StoreTab M2M map
    // finishes loading, this effect re-runs and re-classifies with the new
    // taxonomy — so we don't block the UI on a slow/failed M2M lookup.
    fetchProducts()
  }, [kasoma, pomolo, cabinetPriceGroup, selectedPosition, mode, storeTab.loaded])

  // When model changes in non-cabinet modes, reset selection (product may not exist in new model)
  useEffect(() => {
    if (!isCabinetMode && priceGroup) {
      setSelectedProduct(null)
      setSelectedProductName(null)
      setFilterW(""); setFilterD(""); setFilterH("")
    }
  }, [priceGroup])

  const fetchProducts = async () => {
    setLoading(true)
    setSelectedProduct(null)
    setSelectedProductName(null)
    setFilterW(""); setFilterD(""); setFilterH("")
    setCustomWEnabled(false); setCustomDEnabled(false); setCustomHEnabled(false)
    setCustomW(""); setCustomD(""); setCustomH("")
    setMeterInput(""); setCubicL(""); setCubicW(""); setCubicH("")
    setDimsExpanded(false)
    try {
      const filters = []

      if (isCabinetMode) {
        // Cabinet mode: only configurable products with cabinet_position
        filters.push("(is_configurable=true)")

        if (selectedPosition) {
          filters.push(`(cabinet_position=${selectedPosition.toUpperCase()})`)
        }

        if (pomolo) {
          if (pomolo === "GOLA") {
            filters.push(`((handle_style=GOLA) OR (handle_style=BOTH))`)
          } else if (pomolo === "OXI GOLA") {
            filters.push(`((handle_style=HANDLE) OR (handle_style=BOTH))`)
          }
        }
      } else {
        // Accessories / Special mode: fetch all configurable products,
        // then client-side keep only NOCODE ones (accessories).
        // These are stored as category=["KITCHEN","subcategory"] with NOCODE finish.
        filters.push("(is_configurable=true)")
      }

      const filterStr = filters.join(" AND ")
      const authUser = JSON.parse(localStorage.getItem("authUser") || "{}")
      const url = `${GET_PRODUCTS}?limit=1000&offset=0&filter=${encodeURIComponent(filterStr)}&order=${encodeURIComponent("name ASC")}`
      const resp = await fetch(url, {
        headers: {
          Accept: "application/json",
          "X-Api-Key": config.APP_API_KEY,
          Authorization: authUser.session_token ? `Bearer ${authUser.session_token}` : "",
        },
      })
      const data = await resp.json()
      let fetched = data.resource || []

      // Separate cabinets / accessories / specials
      if (fetched.length > 0) {
        const isNocode = (p) => {
          if (!p.variants) return false
          try {
            const variants = JSON.parse(p.variants)
            if (!Array.isArray(variants) || variants.length === 0) return false
            return variants.every(v => !v.finish_code || v.finish_code === "NOCODE")
          } catch { return false }
        }

        const hasCategory = (p, cat) => {
          const cats = parseCategories(p.category)
          return cats.some(c => c.toUpperCase() === cat.toUpperCase())
        }

        // Pagkos ids: union of the legacy Position category (option_value=pagkos)
        // and the new StoreTab "Pagkos" mapping. Fetching the legacy set once
        // here preserves the pre-migration behavior while StoreTab overlays it.
        let legacyPagkosIds = new Set()
        if (!isCabinetMode) {
          try {
            const optResp = await fetch(
              `${config.APP_URL}_table/product_category_options?filter=${encodeURIComponent("(option_value=pagkos)")}&limit=1`,
              { headers: { Accept: "application/json", "X-Api-Key": config.APP_API_KEY, Authorization: authUser.session_token ? `Bearer ${authUser.session_token}` : "" } }
            )
            const optData = await optResp.json()
            const pagkosOption = (optData.resource || [])[0]
            if (pagkosOption) {
              const mapResp = await fetch(
                `${config.APP_URL}_table/product_categories_mapping?filter=${encodeURIComponent(`(category_option_id=${pagkosOption.id})`)}&limit=1000`,
                { headers: { Accept: "application/json", "X-Api-Key": config.APP_API_KEY, Authorization: authUser.session_token ? `Bearer ${authUser.session_token}` : "" } }
              )
              const mapData = await mapResp.json()
              legacyPagkosIds = new Set((mapData.resource || []).map(m => m.product_id))
            }
          } catch (err) {
            console.error("[ProductGrid] Error fetching pagkos category mapping:", err)
          }
        }

        // Classifiers — M2M-first, legacy-JSON fallback. `storeTab` sets come
        // from useStoreTabCategoryMap; empty sets during load fall through
        // cleanly so behavior on a fresh install matches the pre-M2M state.
        const isPagkos    = (p) => storeTab.pagkos.has(p.id) || legacyPagkosIds.has(p.id)
        const isCladding  = (p) => storeTab.cladding.has(p.id) || hasCategory(p, "ΠΟΡΤΑ ΕΠΕΝΔΥΣΗΣ") || hasCategory(p, "Door")
        const isSpecial   = (p) => storeTab.special.has(p.id) || hasCategory(p, "Special")
        const isAccessory = (p) => storeTab.accessories.has(p.id) || hasCategory(p, "Accessories")

        if (isCabinetMode) {
          fetched = fetched.filter(p => !isNocode(p))
        } else if (isPagkosMode) {
          fetched = fetched.filter(p => isPagkos(p))
        } else if (isDoorCladdingMode) {
          fetched = fetched.filter(p => isCladding(p))
        } else if (isSpecialMode) {
          fetched = fetched.filter(p => isSpecial(p) && !isCladding(p) && !isPagkos(p))
        } else {
          fetched = fetched.filter(p => isAccessory(p) && !isSpecial(p) && !isPagkos(p))
        }
      }

      setProducts(fetched)
    } catch (err) {
      console.error("[ProductGrid] Error fetching:", err)
      setProducts([])
    } finally {
      setLoading(false)
    }
  }

  // Unique product names — filtered by model (priceGroup/finish_code) when set
  const uniqueProductNames = useMemo(() => {
    const names = new Set()
    products.forEach((p) => {
      if (!p.name) return
      // If a model is selected, only show products with at least one matching variant
      if (priceGroup) {
        try {
          const variants = JSON.parse(p.variants || "[]")
          if (!Array.isArray(variants)) return
          // Skip filtering for NOCODE products (accessories) — they're model-agnostic
          const isNocodeProd = variants.every(v => !v.finish_code || v.finish_code === "NOCODE")
          if (!isNocodeProd) {
            const hasMatch = variants.some(v => v.finish_code === priceGroup)
            if (!hasMatch) return
          }
        } catch { return }
      }
      names.add(p.name)
    })
    return Array.from(names).sort()
  }, [products, priceGroup])

  // In accessories mode, group product names by subcategory (category[1])
  // Uses uniqueProductNames to respect model filter
  const subcategoryGroups = useMemo(() => {
    if (isCabinetMode) return null
    const allowedNames = new Set(uniqueProductNames)
    const groups = {}
    products.forEach((p) => {
      if (!p.name || !allowedNames.has(p.name)) return
      let cats = []
      try { cats = JSON.parse(p.category || "[]") } catch { cats = [] }
      const sub = (cats.length > 1 ? cats[1] : "ΑΛΛΑ") || "ΑΛΛΑ"
      if (!groups[sub]) groups[sub] = new Set()
      groups[sub].add(p.name)
    })
    // Convert sets to sorted arrays
    const result = {}
    for (const [sub, nameSet] of Object.entries(groups)) {
      result[sub] = Array.from(nameSet).sort()
    }
    return result
  }, [products, isCabinetMode, uniqueProductNames])

  // Filter by product name
  const nameFiltered = useMemo(() => {
    if (!selectedProductName) return products
    return products.filter(p => p.name === selectedProductName)
  }, [products, selectedProductName])

  // Parse variants for all name-filtered products
  const variantsMap = useMemo(() => {
    const map = new Map()
    for (const p of nameFiltered) {
      let variants = []
      try { variants = JSON.parse(p.variants || "[]") } catch { /* ignore */ }
      if (Array.isArray(variants) && variants.length > 0) {
        map.set(p.id, variants)
      }
    }
    return map
  }, [nameFiltered])

  // Check if the selected product name has variants
  const hasVariants = useMemo(() => {
    if (!selectedProductName) return false
    for (const p of nameFiltered) {
      if (variantsMap.has(p.id)) return true
    }
    return false
  }, [nameFiltered, variantsMap, selectedProductName])

  // Kasoma/pomolo → variant field mappings
  const KASOMA_CORE_MAP = { "Basic": "Unicolor", "Wood-Grain": "Woodgrain" }
  const POMOLO_HANDLE_MAP = { "GOLA": "GOLA", "OXI GOLA": "HANDLE" }
  const activeCoreFilter = kasoma ? KASOMA_CORE_MAP[kasoma] : null
  const activeHandleFilter = pomolo ? POMOLO_HANDLE_MAP[pomolo] : null

  // Collect all variants for the selected product name, filtered by model only.
  // core_material + handle_style are NOT filtered here — they only affect pricing
  // (in findMatchingVariant) so that all physical sizes remain visible even when
  // the specific kasoma/pomolo combination has no pricing yet.
  // Includes variants with OR without price so dimensions are always discoverable.
  const allVariants = useMemo(() => {
    if (!hasVariants) return []
    const result = []
    for (const p of nameFiltered) {
      const variants = variantsMap.get(p.id) || []
      for (const v of variants) {
        if (priceGroup && v.finish_code && v.finish_code !== "NOCODE" && v.finish_code !== priceGroup) continue
        result.push({ ...v, _parentProduct: p })
      }
    }
    return result
  }, [nameFiltered, variantsMap, hasVariants, priceGroup])

  // When no model selected, show all products; when selected, deduplicate by dimensions
  const uniqueProducts = useMemo(() => {
    if (!selectedProductName) return products
    const seen = new Map()
    for (const p of nameFiltered) {
      const key = `${p.productDepth || 0}-${p.productHeight || 0}`
      if (!seen.has(key)) seen.set(key, p)
    }
    return Array.from(seen.values())
  }, [products, nameFiltered, selectedProductName])

  // ── Cascading dimension filters: W → D → H ──
  // Step 1: All available widths
  const availableWidths = useMemo(() => {
    const vals = new Set()
    if (hasVariants) {
      allVariants.forEach(v => { if (v.width) vals.add(parseInt(v.width)) })
    } else {
      nameFiltered.forEach(p => { if (p.productWidth) vals.add(parseInt(p.productWidth)) })
    }
    return Array.from(vals).sort((a, b) => a - b)
  }, [nameFiltered, hasVariants, allVariants])

  // Step 2: Available depths — narrowed by selected W
  const availableDepths = useMemo(() => {
    if (!filterW) return []
    const wVal = parseInt(filterW)
    const vals = new Set()
    if (hasVariants) {
      allVariants.forEach(v => {
        if (parseInt(v.width) === wVal && v.depth) vals.add(parseInt(v.depth))
      })
    } else {
      nameFiltered.forEach(p => {
        if (parseInt(p.productWidth) === wVal && p.productDepth) vals.add(parseInt(p.productDepth))
      })
    }
    return Array.from(vals).sort((a, b) => a - b)
  }, [nameFiltered, hasVariants, allVariants, filterW])

  // Step 3: Available heights — narrowed by selected W + D
  const availableHeights = useMemo(() => {
    if (!filterW || !filterD) return []
    const wVal = parseInt(filterW)
    const dVal = parseInt(filterD)
    const vals = new Set()
    if (hasVariants) {
      allVariants.forEach(v => {
        if (parseInt(v.width) === wVal && parseInt(v.depth) === dVal && v.height) vals.add(parseInt(v.height))
      })
    } else {
      nameFiltered.forEach(p => {
        if (parseInt(p.productWidth) === wVal && parseInt(p.productDepth) === dVal && p.productHeight) vals.add(parseInt(p.productHeight))
      })
    }
    return Array.from(vals).sort((a, b) => a - b)
  }, [nameFiltered, hasVariants, allVariants, filterW, filterD])

  // Auto-select when only one option at each step
  useEffect(() => {
    if (availableWidths.length === 1 && !filterW) setFilterW(String(availableWidths[0]))
  }, [availableWidths])
  useEffect(() => {
    if (availableDepths.length === 1 && !filterD) setFilterD(String(availableDepths[0]))
  }, [availableDepths])
  useEffect(() => {
    if (availableHeights.length === 1 && !filterH) setFilterH(String(availableHeights[0]))
  }, [availableHeights])

  // Filter products by all three dimensions
  const filteredProducts = useMemo(() => {
    let filtered = uniqueProducts
    const wVal = filterW ? parseInt(filterW) : null
    const dVal = filterD ? parseInt(filterD) : null
    const hVal = filterH ? parseInt(filterH) : null
    if (!wVal && !dVal && !hVal) return filtered
    if (hasVariants) {
      filtered = filtered.filter(p => {
        const variants = variantsMap.get(p.id) || []
        return variants.some(v => {
          const wOk = !wVal || parseInt(v.width) === wVal
          const dOk = !dVal || parseInt(v.depth) === dVal
          const hOk = !hVal || parseInt(v.height) === hVal
          const finishOk = !priceGroup || !v.finish_code || v.finish_code === "NOCODE" || v.finish_code === priceGroup
          return wOk && dOk && hOk && finishOk
        })
      })
    } else {
      filtered = filtered.filter(p => {
        const wOk = !wVal || parseInt(p.productWidth) === wVal
        const dOk = !dVal || parseInt(p.productDepth) === dVal
        const hOk = !hVal || parseInt(p.productHeight) === hVal
        return wOk && dOk && hOk
      })
    }
    return filtered
  }, [uniqueProducts, filterW, filterD, filterH, hasVariants, variantsMap, priceGroup])

  // Fetch door options for any product with door mappings
  useEffect(() => {
    if (!selectedProduct) {
      setDoorOptions([])
      setSelectedDoor(null)
      return
    }
    const fetchDoorOptions = async () => {
      try {
        const authUser = JSON.parse(localStorage.getItem("authUser") || "{}")
        const headers = {
          Accept: "application/json",
          "X-Api-Key": config.APP_API_KEY,
          Authorization: authUser.session_token ? `Bearer ${authUser.session_token}` : "",
        }

        // 1. Fetch product's category mappings
        const mapResp = await fetch(
          `${config.APP_URL}_table/product_categories_mapping?filter=${encodeURIComponent(`(product_id=${selectedProduct.id})`)}&limit=1000`,
          { headers }
        )
        const mapData = await mapResp.json()
        const mappings = mapData.resource || []
        if (mappings.length === 0) { setDoorOptions([]); return }

        // 2. Fetch all Doors category options (category_id=10), cache once
        let allDoorOpts = doorOptionsCache
        if (!allDoorOpts) {
          const optResp = await fetch(
            `${config.APP_URL}_table/product_category_options?filter=${encodeURIComponent("(category_id=10)")}&limit=100`,
            { headers }
          )
          const optData = await optResp.json()
          allDoorOpts = optData.resource || []
          setDoorOptionsCache(allDoorOpts)
        }

        // 3. Intersect: only door options that appear in product's mappings
        const mappedOptionIds = new Set(mappings.map(m => m.category_option_id))
        const matched = allDoorOpts.filter(opt => mappedOptionIds.has(opt.id))
        setDoorOptions(matched)
        setSelectedDoor(null)
      } catch (err) {
        console.error("[ProductGrid] Error fetching door options:", err)
        setDoorOptions([])
      }
    }
    fetchDoorOptions()
  }, [selectedProduct?.id])

  // Fetch Model atr1 for hinge pricing when model (priceGroup) changes
  useEffect(() => {
    if (!priceGroup) { setModelAtr1(null); return }
    const fetchModelAtr1 = async () => {
      try {
        let opts = modelOptionsCache
        if (!opts) {
          const authUser = JSON.parse(localStorage.getItem("authUser") || "{}")
          const resp = await fetch(
            `${config.APP_URL}_table/product_category_options?filter=${encodeURIComponent("(category_id=4)")}&limit=100`,
            { headers: { Accept: "application/json", "X-Api-Key": config.APP_API_KEY, Authorization: authUser.session_token ? `Bearer ${authUser.session_token}` : "" } }
          )
          const data = await resp.json()
          opts = data.resource || []
          setModelOptionsCache(opts)
        }
        const match = opts.find(o => o.option_name === priceGroup)
        setModelAtr1(match?.atr1 ? parseFloat(match.atr1) : null)
      } catch (err) {
        console.error("[ProductGrid] Error fetching model atr1:", err)
        setModelAtr1(null)
      }
    }
    fetchModelAtr1()
  }, [priceGroup])

  // Reset hinge + qty state when product changes
  useEffect(() => {
    setHingedEnabled(false)
    setHingeCm("")
    setAddQty(1)
  }, [selectedProduct?.id])

  // Calculate hinge surcharge — 4cm doubles the base rate, 2cm is base.
  const hingeSurcharge = useMemo(() => {
    if (!hingedEnabled || !selectedDoor || modelAtr1 == null) return 0
    const doorCount = (selectedDoor.option_name || "").startsWith("2") ? 2 : 1
    const cmMultiplier = hingeCm === "4" ? 2 : 1
    return modelAtr1 * doorCount * cmMultiplier
  }, [hingedEnabled, selectedDoor, modelAtr1, hingeCm])

  const activeSwatch = swatch1

  // Helper: find best matching variant for a product
  const findMatchingVariant = (productId) => {
    const variants = variantsMap.get(productId) || []
    if (variants.length === 0) return null

    // Only consider variants with a price
    const priced = variants.filter(v => v.b2bsalesprice != null)
    if (priced.length === 0) return null

    const wVal = filterW ? parseInt(filterW) : null
    const dVal = filterD ? parseInt(filterD) : null
    const hVal = filterH ? parseInt(filterH) : null

    // For accessories: match on per-axis attribute filters (Χαρακτηριστικό picker).
    // attributeFilters: { finish_code: "100W", ... }
    const attrMatch = (v) => {
      for (const [field, value] of Object.entries(attributeFilters)) {
        if (!value) continue
        if (String(v[field] || "") !== String(value)) return false
      }
      return true
    }

    // Best match: all filters including kasoma + pomolo + attributes
    return priced.find(v => {
      const wOk = !wVal || parseInt(v.width) === wVal
      const dOk = !dVal || parseInt(v.depth) === dVal
      const hOk = !hVal || parseInt(v.height) === hVal
      const finishMatch = !priceGroup || !v.finish_code || v.finish_code === "NOCODE" || v.finish_code === priceGroup
      const coreMatch = !activeCoreFilter || !v.core_material || v.core_material === activeCoreFilter
      const handleMatch = !activeHandleFilter || !v.handle_style || v.handle_style === activeHandleFilter
      return wOk && dOk && hOk && finishMatch && coreMatch && handleMatch && attrMatch(v)
    }) || priced.find(v => {
      // Fallback: without kasoma/pomolo filters
      const wOk = !wVal || parseInt(v.width) === wVal
      const dOk = !dVal || parseInt(v.depth) === dVal
      const hOk = !hVal || parseInt(v.height) === hVal
      const finishMatch = !priceGroup || !v.finish_code || v.finish_code === "NOCODE" || v.finish_code === priceGroup
      return wOk && dOk && hOk && finishMatch && attrMatch(v)
    }) || null
  }

  // Compute attribute axes for the selected accessory product — fields whose values
  // differ across its priced variants. Each axis becomes a chip-picker in the UI.
  const accessoryAxes = useMemo(() => {
    if (mode !== "Accessories" || !selectedProduct?.id) return []
    const variants = (variantsMap.get(selectedProduct.id) || []).filter(v => v.b2bsalesprice != null)
    if (variants.length < 2) return []
    const candidates = ["finish_code", "core_material", "handle_style"]
    const axes = []
    for (const field of candidates) {
      const values = new Set()
      for (const v of variants) {
        const val = v[field]
        if (val && val !== "NOCODE" && val !== "") values.add(val)
      }
      if (values.size >= 2) axes.push({ field, values: [...values].sort() })
    }
    return axes
  }, [mode, selectedProduct?.id, variantsMap])

  // Reset attribute filters when product changes
  useEffect(() => {
    setAttributeFilters({})
  }, [selectedProduct?.id])

  const handleAdd = () => {
    if (!selectedProduct) return

    // Always try to find a matching variant when product has variants
    let variantOverride = null
    if (hasVariants) {
      const match = findMatchingVariant(selectedProduct.id)
      if (match) {
        variantOverride = {
          sku: match.sku || selectedProduct.sku,
          salesprice: match.salesprice || selectedProduct.salesprice,
          b2bsalesprice: match.b2bsalesprice || selectedProduct.b2bsalesprice,
          productWidth: match.width || selectedProduct.productWidth,
          productDepth: match.depth || selectedProduct.productDepth,
          productHeight: match.height || selectedProduct.productHeight,
        }
      }
    }

    // Build final dimensions (use custom if enabled and valid, else standard)
    const finalW = customWEnabled && customW && parseInt(customW) > 0 && parseInt(customW) < parseInt(filterW)
      ? parseInt(customW) : (filterW ? parseInt(filterW) : null)
    const finalD = customDEnabled && customD && parseInt(customD) > 0 && parseInt(customD) < parseInt(filterD)
      ? parseInt(customD) : (filterD ? parseInt(filterD) : null)
    const finalH = customHEnabled && customH && parseInt(customH) > 0 && parseInt(customH) < parseInt(filterH)
      ? parseInt(customH) : (filterH ? parseInt(filterH) : null)

    // Build unit data for Meter / CubicM products
    let unitData = undefined
    if (isMeterUnit && meterInput) {
      const rate = variantOverride
        ? parseFloat(variantOverride.b2bsalesprice || selectedProduct.b2bsalesprice) || 0
        : parseFloat(selectedProduct.b2bsalesprice) || 0
      unitData = {
        unit: "Meter",
        unitRate: rate,
        unitQuantity: parseFloat(meterInput),
      }
    } else if (isCubicUnit && cubicL && cubicW && cubicH) {
      const rate = variantOverride
        ? parseFloat(variantOverride.b2bsalesprice || selectedProduct.b2bsalesprice) || 0
        : parseFloat(selectedProduct.b2bsalesprice) || 0
      const vol = (parseFloat(cubicL) * parseFloat(cubicW) * parseFloat(cubicH)) / 1000000000
      unitData = {
        unit: "CubicM",
        unitRate: rate,
        unitQuantity: vol,
        cubicDimensions: { l: parseFloat(cubicL), w: parseFloat(cubicW), h: parseFloat(cubicH) },
      }
    }

    onAddProduct(selectedProduct, {
      swatchOverride: swatch1,
      doorModelOverride: doorModel,
      customRalCode: customRalCode || "",
      kasoma,
      pomolo,
      modelCode: priceGroup || "",
      variantOverride,
      customDimensionsOverride:
        filterW || filterD || filterH
          ? {
              length: finalW,
              depth: finalD,
              height: finalH,
            }
          : null,
      customSizeSurcharge: customSurcharge > 0 ? customSurcharge : 0,
      unitData,
      doorData: selectedDoor ? {
        optionId: selectedDoor.id,
        optionName: selectedDoor.option_name || selectedDoor.name,
        surcharge: 0,
      } : undefined,
      hingeData: hingedEnabled && selectedDoor ? {
        cm: parseFloat(hingeCm) || 0,
        surcharge: hingeSurcharge,
        modelAtr1: modelAtr1 || 0,
        doorCount: (selectedDoor.option_name || "").startsWith("2") ? 2 : 1,
      } : undefined,
      quantity: showAddQty ? addQty : 1,
    })
    setSelectedProduct(null)
    setSelectedDoor(null)
    setHingedEnabled(false)
    setHingeCm("")
    setMeterInput("")
    setCubicL("")
    setCubicW("")
    setCubicH("")
    setAddQty(1)
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    )
  }

  if (!products || products.length === 0) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#94a3b8" }}>
        <Typography variant="body2">
          {isCabinetMode && (!kasoma || !pomolo || !priceGroup)
            ? t("Επιλεξτε Μοντελο για να δειτε προιοντα")
            : t("No products found for this selection")}
        </Typography>
      </Box>
    )
  }

  // Show product cards only after all 3 dimensions are selected
  const dimensionsReady = selectedProductName && (
    (availableWidths.length === 0 || filterW) &&
    (availableDepths.length === 0 || filterD) &&
    (availableHeights.length === 0 || filterH)
  )
  const showProducts = selectedProductName && dimensionsReady

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Step 1: Product name selector — always visible */}
      <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid #e2e8f0", bgcolor: "#f8fafc", maxHeight: 280, overflow: "auto" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.2 }}>
          <Typography
            sx={{ color: "#475569", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}
          >
            {t("Product")} ({uniqueProductNames.length})
          </Typography>
          {selectedProductName && (
            <Chip
              label={t("Clear")}
              size="small"
              onClick={() => { setSelectedProductName(null); setFilterW(""); setFilterD(""); setFilterH(""); setCustomWEnabled(false); setCustomDEnabled(false); setCustomHEnabled(false); setCustomW(""); setCustomD(""); setCustomH(""); setMeterInput(""); setCubicL(""); setCubicW(""); setCubicH(""); setDimsExpanded(false) }}
              sx={{ fontSize: "0.65rem", height: 22, bgcolor: "#fecaca", color: "#7f1d1d", fontWeight: 600, cursor: "pointer" }}
            />
          )}
        </Stack>

        {/* Accessories mode: group by subcategory */}
        {subcategoryGroups ? (
          <Stack spacing={1}>
            {Object.entries(subcategoryGroups).sort(([a], [b]) => a.localeCompare(b)).map(([sub, names]) => (
              <Box key={sub}>
                <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", mb: 0.4, letterSpacing: 0.5 }}>
                  {sub} ({names.length})
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {names.map((name) => {
                    const isSelected = selectedProductName === name
                    return (
                      <Chip
                        key={name}
                        label={name}
                        size="small"
                        variant={isSelected ? "filled" : "outlined"}
                        color={isSelected ? "primary" : "default"}
                        onClick={() => {
                          setSelectedProductName(selectedProductName === name ? null : name)
                          setFilterW("")
                          setFilterD("")
                          setFilterH("")
                        }}
                        sx={{
                          fontSize: "0.7rem",
                          height: 26,
                          fontWeight: 600,
                          cursor: "pointer",
                          minWidth: 0,
                          borderWidth: 1,
                          transition: "all 0.15s",
                          ...(isSelected && { bgcolor: "#d97706", color: "#fff" }),
                          "&:hover": { bgcolor: isSelected ? "#b45309" : "#fef3c7" },
                        }}
                      />
                    )
                  })}
                </Box>
              </Box>
            ))}
          </Stack>
        ) : (
          /* Cabinet mode: flat list */
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {uniqueProductNames.map((name) => {
              const isSelected = selectedProductName === name
              return (
                <Chip
                  key={name}
                  label={name}
                  size="small"
                  variant={isSelected ? "filled" : "outlined"}
                  color={isSelected ? "primary" : "default"}
                  onClick={() => {
                    setSelectedProductName(selectedProductName === name ? null : name)
                    setFilterW("")
                    setFilterD("")
                    setFilterH("")
                  }}
                  sx={{
                    fontSize: "0.7rem",
                    height: 26,
                    fontWeight: 600,
                    cursor: "pointer",
                    minWidth: 0,
                    borderWidth: 1,
                    transition: "all 0.15s",
                    ...(isSelected && { bgcolor: "#3b82f6", color: "#fff" }),
                    "&:hover": { bgcolor: isSelected ? "#2563eb" : "#f1f5f9" },
                  }}
                />
              )
            })}
          </Box>
        )}
      </Box>

      {/* Accessories: Χαρακτηριστικό (Attribute) picker — one chip strip per varying axis */}
      {selectedProductName && accessoryAxes.length > 0 && (
        <Box sx={{ borderBottom: "1px solid #e2e8f0", bgcolor: "#fff", px: 2.5, py: 1.5 }}>
          {accessoryAxes.map(axis => (
            <Box key={axis.field} sx={{ mb: 1.25, "&:last-of-type": { mb: 0 } }}>
              <Typography sx={{ color: "#64748b", fontSize: "0.7rem", fontWeight: 700, mb: 0.6, textTransform: "uppercase" }}>
                {t("Χαρακτηριστικο")} — <span style={{ fontWeight: 500, textTransform: "none" }}>{axis.field}</span>
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {axis.values.map(val => {
                  const isActive = attributeFilters[axis.field] === val
                  return (
                    <Chip
                      key={val}
                      label={val}
                      size="small"
                      onClick={() => setAttributeFilters(prev => ({
                        ...prev,
                        [axis.field]: isActive ? "" : val,
                      }))}
                      variant={isActive ? "filled" : "outlined"}
                      sx={{
                        fontSize: "0.7rem",
                        height: 26,
                        fontWeight: 600,
                        cursor: "pointer",
                        ...(isActive
                          ? { bgcolor: "#d97706", color: "#fff", borderColor: "transparent" }
                          : { borderColor: "#fbbf24", color: "#92400e" }),
                        "&:hover": isActive ? {} : { bgcolor: "#fef3c7" },
                      }}
                    />
                  )
                })}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Step 2: Cascading W → D → H selectors with custom size option */}
      {selectedProductName && availableWidths.length > 0 && (
        <Box sx={{ borderBottom: "1px solid #e2e8f0", bgcolor: "#fff" }}>
          {/* Collapsed summary + expand toggle when product is selected */}
          {selectedProduct && dimensionsReady && (
            <Box
              onClick={() => setDimsExpanded(prev => !prev)}
              sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                px: 2.5, py: 0.5, cursor: "pointer",
                "&:hover": { bgcolor: "#f8fafc" },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#475569" }}>
                  {filterW && `W:${filterW}`}{filterD && ` D:${filterD}`}{filterH && ` H:${filterH}`} mm
                </Typography>
                {customSurcharge > 0 && (
                  <Chip label={`+€${customSurcharge}`} size="small" sx={{ height: 18, fontSize: "0.55rem", fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e" }} />
                )}
              </Stack>
              {dimsExpanded ? <ExpandLessIcon sx={{ fontSize: 16, color: "#94a3b8" }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: "#94a3b8" }} />}
            </Box>
          )}
          <Collapse in={!selectedProduct || !dimensionsReady || dimsExpanded}>
          <Box sx={{ px: 2.5, py: 1.75 }}>
          {/* Width */}
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ color: "#64748b", fontSize: "0.7rem", fontWeight: 700, mb: 0.6, textTransform: "uppercase" }}>
              W — {t("Πλατος")} (mm)
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {availableWidths.map((w) => {
                const isSelected = filterW && parseInt(filterW) === w
                return (
                  <Chip
                    key={w}
                    label={`${w}`}
                    size="small"
                    variant={isSelected ? "filled" : "outlined"}
                    color={isSelected ? "primary" : "default"}
                    onClick={() => {
                      if (isSelected) {
                        setFilterW(""); setFilterD(""); setFilterH("")
                        setCustomWEnabled(false); setCustomW("")
                        setCustomDEnabled(false); setCustomD("")
                        setCustomHEnabled(false); setCustomH("")
                      } else {
                        setFilterW(String(w)); setFilterD(""); setFilterH("")
                        setCustomWEnabled(false); setCustomW("")
                        setCustomDEnabled(false); setCustomD("")
                        setCustomHEnabled(false); setCustomH("")
                      }
                    }}
                    sx={{ fontSize: "0.72rem", height: 30, cursor: "pointer", fontWeight: isSelected ? 700 : 500, minWidth: 55 }}
                  />
                )
              })}
            </Box>
            {/* Custom Width toggle */}
            {isCabinetMode && filterW && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.75 }}>
                <Switch
                  size="small"
                  checked={customWEnabled}
                  onChange={(e) => { setCustomWEnabled(e.target.checked); if (!e.target.checked) setCustomW("") }}
                  sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#d97706" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#d97706" } }}
                />
                <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: customWEnabled ? "#d97706" : "#94a3b8" }}>
                  {t("Custom")}
                </Typography>
                {customWEnabled && (
                  <>
                    <TextField
                      size="small"
                      type="number"
                      value={customW}
                      onChange={(e) => setCustomW(e.target.value)}
                      placeholder={`< ${filterW}`}
                      inputProps={{ min: 1, max: parseInt(filterW) - 1 }}
                      sx={{ width: 90, "& .MuiInputBase-root": { height: 28, fontSize: "0.75rem" } }}
                      error={!customW || parseInt(customW) >= parseInt(filterW) || parseInt(customW) <= 0}
                      helperText={!customW ? t("Υποχρεωτικό") : parseInt(customW) >= parseInt(filterW) ? `< ${filterW}` : ""}
                      FormHelperTextProps={{ sx: { fontSize: "0.55rem", mt: 0, ml: 0 } }}
                    />
                    <Chip
                      label={`+€${CUSTOM_SURCHARGE.W}`}
                      size="small"
                      sx={{ height: 20, fontSize: "0.62rem", fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e" }}
                    />
                  </>
                )}
              </Stack>
            )}
          </Box>

          {/* Depth — only after W selected */}
          {filterW && availableDepths.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ color: "#64748b", fontSize: "0.7rem", fontWeight: 700, mb: 0.6, textTransform: "uppercase" }}>
                D — {t("Βαθος")} (mm)
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {availableDepths.map((d) => {
                  const isSelected = filterD && parseInt(filterD) === d
                  return (
                    <Chip
                      key={d}
                      label={`${d}`}
                      size="small"
                      variant={isSelected ? "filled" : "outlined"}
                      color={isSelected ? "primary" : "default"}
                      onClick={() => {
                        if (isSelected) {
                          setFilterD(""); setFilterH("")
                          setCustomDEnabled(false); setCustomD("")
                          setCustomHEnabled(false); setCustomH("")
                        } else {
                          setFilterD(String(d)); setFilterH("")
                          setCustomDEnabled(false); setCustomD("")
                          setCustomHEnabled(false); setCustomH("")
                        }
                      }}
                      sx={{ fontSize: "0.72rem", height: 30, cursor: "pointer", fontWeight: isSelected ? 700 : 500, minWidth: 55 }}
                    />
                  )
                })}
              </Box>
              {/* Custom Depth toggle */}
              {isCabinetMode && filterD && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.75 }}>
                  <Switch
                    size="small"
                    checked={customDEnabled}
                    onChange={(e) => { setCustomDEnabled(e.target.checked); if (!e.target.checked) setCustomD("") }}
                    sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#d97706" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#d97706" } }}
                  />
                  <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: customDEnabled ? "#d97706" : "#94a3b8" }}>
                    {t("Custom")}
                  </Typography>
                  {customDEnabled && (
                    <>
                      <TextField
                        size="small"
                        type="number"
                        value={customD}
                        onChange={(e) => setCustomD(e.target.value)}
                        placeholder={`< ${filterD}`}
                        inputProps={{ min: 1, max: parseInt(filterD) - 1 }}
                        sx={{ width: 90, "& .MuiInputBase-root": { height: 28, fontSize: "0.75rem" } }}
                        error={!customD || parseInt(customD) >= parseInt(filterD) || parseInt(customD) <= 0}
                        helperText={!customD ? t("Υποχρεωτικό") : parseInt(customD) >= parseInt(filterD) ? `< ${filterD}` : ""}
                        FormHelperTextProps={{ sx: { fontSize: "0.55rem", mt: 0, ml: 0 } }}
                      />
                      <Chip
                        label={`+€${CUSTOM_SURCHARGE.D}`}
                        size="small"
                        sx={{ height: 20, fontSize: "0.62rem", fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e" }}
                      />
                    </>
                  )}
                </Stack>
              )}
            </Box>
          )}

          {/* Height — only after W + D selected */}
          {filterW && filterD && availableHeights.length > 0 && (
            <Box>
              <Typography sx={{ color: "#64748b", fontSize: "0.7rem", fontWeight: 700, mb: 0.6, textTransform: "uppercase" }}>
                H — {t("Υψος")} (mm)
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {availableHeights.map((h) => {
                  const isSelected = filterH && parseInt(filterH) === h
                  return (
                    <Chip
                      key={h}
                      label={`${h}`}
                      size="small"
                      variant={isSelected ? "filled" : "outlined"}
                      color={isSelected ? "primary" : "default"}
                      onClick={() => {
                        setFilterH(isSelected ? "" : String(h))
                        setCustomHEnabled(false); setCustomH("")
                      }}
                      sx={{ fontSize: "0.72rem", height: 30, cursor: "pointer", fontWeight: isSelected ? 700 : 500, minWidth: 55 }}
                    />
                  )
                })}
              </Box>
              {/* Custom Height toggle */}
              {isCabinetMode && filterH && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.75 }}>
                  <Switch
                    size="small"
                    checked={customHEnabled}
                    onChange={(e) => { setCustomHEnabled(e.target.checked); if (!e.target.checked) setCustomH("") }}
                    sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#d97706" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#d97706" } }}
                  />
                  <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: customHEnabled ? "#d97706" : "#94a3b8" }}>
                    {t("Custom")}
                  </Typography>
                  {customHEnabled && (
                    <>
                      <TextField
                        size="small"
                        type="number"
                        value={customH}
                        onChange={(e) => setCustomH(e.target.value)}
                        placeholder={`< ${filterH}`}
                        inputProps={{ min: 1, max: parseInt(filterH) - 1 }}
                        sx={{ width: 90, "& .MuiInputBase-root": { height: 28, fontSize: "0.75rem" } }}
                        error={!customH || parseInt(customH) >= parseInt(filterH) || parseInt(customH) <= 0}
                        helperText={!customH ? t("Υποχρεωτικό") : parseInt(customH) >= parseInt(filterH) ? `< ${filterH}` : ""}
                        FormHelperTextProps={{ sx: { fontSize: "0.55rem", mt: 0, ml: 0 } }}
                      />
                      <Chip
                        label={`+€${CUSTOM_SURCHARGE.H}`}
                        size="small"
                        sx={{ height: 20, fontSize: "0.62rem", fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e" }}
                      />
                    </>
                  )}
                </Stack>
              )}
            </Box>
          )}

          {/* Total custom surcharge summary */}
          {customSurcharge > 0 && (
            <Box sx={{ mt: 1.5, pt: 1, borderTop: "1px dashed #e2e8f0" }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#92400e" }}>
                  {t("Custom Size Surcharge")}
                </Typography>
                <Chip
                  label={`+€${customSurcharge.toFixed(2)}`}
                  size="small"
                  sx={{ height: 24, fontSize: "0.72rem", fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e", borderLeft: "3px solid #d97706" }}
                />
              </Stack>
            </Box>
          )}
          </Box>
          </Collapse>
        </Box>
      )}

      {/* Step 3: Product cards */}
      <Box sx={{ flex: 1, overflow: "auto", p: 1.5 }}>
        {selectedProductName && !dimensionsReady && (
          <Box sx={{ textAlign: "center", py: 4, color: "#94a3b8" }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {t("Επιλεξτε διαστασεις για να δειτε προιοντα")}
            </Typography>
          </Box>
        )}
        {showProducts && (
          <>
            {filteredProducts.length === 0 ? (
              <Typography variant="body2" sx={{ color: "#94a3b8", textAlign: "center", py: 4 }}>
                {t("No products found")}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {filteredProducts.map((product) => {
                  const isSelected = selectedProduct?.id === product.id
                  // Use matching variant data if available
                  const variantMatch = hasVariants ? findMatchingVariant(product.id) : null
                  const pW = variantMatch ? (parseInt(variantMatch.width) || 0) : (parseInt(product.productWidth) || 0)
                  const pD = variantMatch ? (parseInt(variantMatch.depth) || 0) : (parseInt(product.productDepth) || 0)
                  const pH = variantMatch ? (parseInt(variantMatch.height) || 0) : (parseInt(product.productHeight) || 0)
                  const displaySku = variantMatch?.sku || product.sku
                  const baseB2B = variantMatch ? (parseFloat(variantMatch.b2bsalesprice) || 0) : (parseFloat(product.b2bsalesprice) || 0)
                  const baseRetail = variantMatch ? (parseFloat(variantMatch.salesprice) || 0) : (parseFloat(product.salesprice) || 0)
                  const displayB2B = baseB2B + customSurcharge + hingeSurcharge
                  const displayRetail = baseRetail + customSurcharge + hingeSurcharge
                  const photo = getFirstPhoto(product.productPhotos)
                  return (
                    <React.Fragment key={product.id}>
                    <Card
                      elevation={0}
                      sx={{
                        border: isSelected ? "2px solid #3b82f6" : "1px solid #e2e8f0",
                        borderRadius: 1.5,
                        cursor: "pointer",
                        transition: "all 0.15s",
                        "&:hover": {
                          borderColor: "#3b82f6",
                          boxShadow: "0 2px 8px rgba(59,130,246,0.1)",
                        },
                      }}
                      onClick={() => setSelectedProduct(product)}
                    >
                      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                        <Stack direction="row" spacing={1.5}>
                          {/* Left: Visual */}
                          <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                            {photo ? (
                              <Box
                                component="img"
                                src={photo}
                                sx={{
                                  width: 64, height: 64, borderRadius: 1,
                                  objectFit: "cover", border: "1px solid #e2e8f0", bgcolor: "#f8fafc",
                                }}
                                onError={(e) => { e.target.style.display = "none" }}
                              />
                            ) : (
                              <BoxDiagram
                                length={pW} height={pH} width={pD} size={64}
                                swatchUrl={activeSwatch?.swatchUrl}
                                productName={product.name}
                              />
                            )}
                            {activeSwatch?.swatchUrl && (
                              <Tooltip title={activeSwatch.label} arrow>
                                <Box
                                  component="img"
                                  src={activeSwatch.swatchUrl}
                                  sx={{
                                    width: 24, height: 24, borderRadius: 0.5,
                                    border: "1px solid #e2e8f0", objectFit: "cover",
                                  }}
                                  onError={(e) => { e.target.style.display = "none" }}
                                />
                              </Tooltip>
                            )}
                          </Box>

                          {/* Right: Info */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#1e293b", lineHeight: 1.3, mb: 0.25 }}
                              title={product.name}
                            >
                              {product.name}
                            </Typography>

                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                              {displaySku && (
                                <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.6rem" }}>
                                  SKU: {displaySku}
                                </Typography>
                              )}
                            </Stack>

                            {product.description && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#64748b", fontSize: "0.65rem",
                                  display: "-webkit-box", WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical", overflow: "hidden",
                                  lineHeight: 1.3, mb: 0.5,
                                }}
                              >
                                {product.description}
                              </Typography>
                            )}

                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                              {(pW > 0 || pH > 0 || pD > 0) && (
                                <Typography variant="caption" sx={{ color: "#475569", fontSize: "0.7rem", fontWeight: 600 }}>
                                  {pW > 0 ? `${t("W")}:${customWEnabled && customW && parseInt(customW) < pW ? customW : pW}` : ""}
                                  {pH > 0 ? ` ${t("H")}:${customHEnabled && customH && parseInt(customH) < pH ? customH : pH}` : ""}
                                  {pD > 0 ? ` ${t("D")}:${customDEnabled && customD && parseInt(customD) < pD ? customD : pD}` : ""}
                                  {" mm"}
                                </Typography>
                              )}
                              {customSurcharge > 0 && (
                                <Chip label={t("Custom")} size="small" sx={{ height: 16, fontSize: "0.5rem", fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e" }} />
                              )}
                            </Stack>

                            <Stack direction="row" spacing={1.5} alignItems="baseline">
                              <Typography sx={{ fontWeight: 700, color: "#3b82f6", fontSize: "0.9rem" }}>
                                €{displayB2B.toFixed(2)}
                                {product.unit === "Meter" && <Typography component="span" sx={{ fontSize: "0.6rem", color: "#64748b" }}>/m</Typography>}
                                {product.unit === "CubicM" && <Typography component="span" sx={{ fontSize: "0.6rem", color: "#64748b" }}>/m³</Typography>}
                              </Typography>
                              {customSurcharge > 0 && (
                                <Typography variant="caption" sx={{ color: "#d97706", fontSize: "0.6rem", fontWeight: 700 }}>
                                  (+€{customSurcharge.toFixed(0)})
                                </Typography>
                              )}
                              {displayRetail > 0 && displayRetail !== displayB2B && (
                                <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.65rem" }}>
                                  {t("Retail")}: €{displayRetail.toFixed(2)}
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>

                    {/* Door & Κρεμαση — inline under selected product */}
                    {isSelected && doorOptions.length > 0 && (
                      <Box sx={{ p: 1.5, bgcolor: "#eff6ff", borderRadius: 1.5, border: "1px solid #bfdbfe" }}>
                        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#1d4ed8", mb: 0.75, textTransform: "uppercase" }}>
                          {t("Πορτα")}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {doorOptions.map((opt) => {
                            const label = opt.option_name || opt.name || `Option ${opt.id}`
                            const isDoorSelected = selectedDoor?.id === opt.id
                            return (
                              <Chip
                                key={opt.id}
                                label={label}
                                size="small"
                                variant={isDoorSelected ? "filled" : "outlined"}
                                onClick={(e) => { e.stopPropagation(); setSelectedDoor(isDoorSelected ? null : opt) }}
                                sx={{
                                  fontSize: "0.72rem",
                                  height: 30,
                                  fontWeight: isDoorSelected ? 700 : 500,
                                  cursor: "pointer",
                                  ...(isDoorSelected
                                    ? { bgcolor: "#1d4ed8", color: "#fff" }
                                    : { borderColor: "#93c5fd", color: "#1e40af" }),
                                  "&:hover": isDoorSelected ? {} : { bgcolor: "#dbeafe" },
                                }}
                              />
                            )
                          })}
                        </Box>
                        {!selectedDoor && (
                          <Typography sx={{ fontSize: "0.6rem", color: "#dc2626", fontWeight: 600, mt: 0.5 }}>
                            {t("Επιλεξτε πορτα")}
                          </Typography>
                        )}

                        {/* Κρεμαση — inline under door options for can_hinged products */}
                        {selectedProduct?.can_hinged && selectedDoor && (
                          <Box sx={{ mt: 1.25, pt: 1.25, borderTop: "1px dashed #bfdbfe" }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Switch
                                size="small"
                                checked={hingedEnabled}
                                onChange={(e) => { setHingedEnabled(e.target.checked); if (!e.target.checked) setHingeCm("") }}
                                sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#7c3aed" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#7c3aed" } }}
                              />
                              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: hingedEnabled ? "#7c3aed" : "#94a3b8" }}>
                                {t("Κρεμαση")}
                              </Typography>
                              {hingedEnabled && modelAtr1 == null && (
                                <Chip
                                  label={t("Χωρις διαφορα")}
                                  size="small"
                                  sx={{ height: 20, fontSize: "0.6rem", fontWeight: 600, bgcolor: "#f1f5f9", color: "#64748b" }}
                                />
                              )}
                            </Stack>
                            {hingedEnabled && (
                              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.75 }}>
                                <ToggleButtonGroup
                                  size="small"
                                  exclusive
                                  value={hingeCm}
                                  onChange={(_, v) => { if (v !== null) setHingeCm(v) }}
                                  sx={{
                                    "& .MuiToggleButton-root": {
                                      height: 30,
                                      px: 1.25,
                                      fontSize: "0.72rem",
                                      fontWeight: 700,
                                      color: "#64748b",
                                      borderColor: "#e2e8f0",
                                    },
                                    "& .MuiToggleButton-root.Mui-selected": {
                                      bgcolor: "#7c3aed",
                                      color: "#fff",
                                      "&:hover": { bgcolor: "#6d28d9" },
                                    },
                                  }}
                                >
                                  <ToggleButton value="2">2 cm</ToggleButton>
                                  <ToggleButton value="4">4 cm</ToggleButton>
                                </ToggleButtonGroup>
                                {hingeCm === "4" && (
                                  <Chip
                                    label="×2"
                                    size="small"
                                    sx={{ height: 20, fontSize: "0.62rem", fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e" }}
                                  />
                                )}
                                {hingeSurcharge > 0 && (
                                  <Chip
                                    label={`+€${hingeSurcharge.toFixed(0)}`}
                                    size="small"
                                    sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700, bgcolor: "#ede9fe", color: "#7c3aed" }}
                                  />
                                )}
                                {modelAtr1 != null && hingeCm && (
                                  <Typography sx={{ fontSize: "0.58rem", color: "#7c3aed" }}>
                                    (€{modelAtr1} × {(selectedDoor.option_name || "").startsWith("2") ? 2 : 1} {t("πορτ.")}
                                    {hingeCm === "4" ? " × 2" : ""})
                                  </Typography>
                                )}
                              </Stack>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}
                  </React.Fragment>
                  )
                })}
              </Stack>
            )}
          </>
        )}
      </Box>

      {/* Bottom bar: unit inputs + add button */}
      {selectedProduct && (
        <Box sx={{ borderTop: "1px solid #e2e8f0", p: 1.5, bgcolor: "#f8fafc" }}>
          {/* Meter / CubicM input row */}
          {isUnitProduct && (
            <Box sx={{ mb: 1.5, p: 1.5, bgcolor: "#fffbeb", borderRadius: 1.5, border: "1px solid #fcd34d" }}>
              {isMeterUnit && (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#92400e", whiteSpace: "nowrap" }}>
                    {t("Μετρα")}:
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={meterInput}
                    onChange={(e) => setMeterInput(e.target.value)}
                    inputProps={{ step: 0.1, min: 0.01 }}
                    placeholder="0.00"
                    error={!meterInput || parseFloat(meterInput) <= 0}
                    sx={{ width: 100, "& .MuiOutlinedInput-root": { fontSize: "0.85rem", height: 34 } }}
                  />
                  <Typography sx={{ fontSize: "0.7rem", color: "#64748b" }}>m</Typography>
                  {meterInput && parseFloat(meterInput) > 0 && (
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#3b82f6" }}>
                      = €{((parseFloat(selectedProduct.b2bsalesprice) || 0) * parseFloat(meterInput)).toFixed(2)}
                    </Typography>
                  )}
                </Stack>
              )}
              {isCubicUnit && (
                <Stack spacing={1}>
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#92400e" }}>
                    {t("Διαστασεις")} (mm):
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      type="number"
                      label={t("Μηκος")}
                      value={cubicL}
                      onChange={(e) => setCubicL(e.target.value)}
                      inputProps={{ min: 1 }}
                      error={!cubicL || parseFloat(cubicL) <= 0}
                      sx={{ width: 90, "& .MuiOutlinedInput-root": { fontSize: "0.8rem", height: 34 }, "& .MuiInputLabel-root": { fontSize: "0.7rem" } }}
                    />
                    <Typography sx={{ color: "#94a3b8" }}>×</Typography>
                    <TextField
                      size="small"
                      type="number"
                      label={t("Πλατος")}
                      value={cubicW}
                      onChange={(e) => setCubicW(e.target.value)}
                      inputProps={{ min: 1 }}
                      error={!cubicW || parseFloat(cubicW) <= 0}
                      sx={{ width: 90, "& .MuiOutlinedInput-root": { fontSize: "0.8rem", height: 34 }, "& .MuiInputLabel-root": { fontSize: "0.7rem" } }}
                    />
                    <Typography sx={{ color: "#94a3b8" }}>×</Typography>
                    <TextField
                      size="small"
                      type="number"
                      label={t("Υψος")}
                      value={cubicH}
                      onChange={(e) => setCubicH(e.target.value)}
                      inputProps={{ min: 1 }}
                      error={!cubicH || parseFloat(cubicH) <= 0}
                      sx={{ width: 90, "& .MuiOutlinedInput-root": { fontSize: "0.8rem", height: 34 }, "& .MuiInputLabel-root": { fontSize: "0.7rem" } }}
                    />
                    {cubicL && cubicW && cubicH && parseFloat(cubicL) > 0 && parseFloat(cubicW) > 0 && parseFloat(cubicH) > 0 && (
                      <Stack>
                        <Typography sx={{ fontSize: "0.65rem", color: "#64748b" }}>
                          {((parseFloat(cubicL) * parseFloat(cubicW) * parseFloat(cubicH)) / 1000000000).toFixed(4)} m³
                        </Typography>
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#3b82f6" }}>
                          = €{((parseFloat(selectedProduct.b2bsalesprice) || 0) * (parseFloat(cubicL) * parseFloat(cubicW) * parseFloat(cubicH)) / 1000000000).toFixed(2)}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              )}
            </Box>
          )}

          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "0.75rem" }}>
                {selectedProduct.name}
              </Typography>
              {activeSwatch ? (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                  {activeSwatch.swatchUrl && (
                    <Box
                      component="img"
                      src={activeSwatch.swatchUrl}
                      sx={{ width: 14, height: 14, borderRadius: "2px", border: "1px solid #e2e8f0", objectFit: "cover" }}
                      onError={(e) => { e.target.style.display = "none" }}
                    />
                  )}
                  <Typography variant="caption" sx={{ fontSize: "0.65rem", color: "#64748b" }}>
                    {activeSwatch.label}
                  </Typography>
                </Stack>
              ) : isCabinetMode && (
                <Typography variant="caption" sx={{ fontSize: "0.65rem", color: "#ef4444", fontWeight: 600 }}>
                  {t("Επιλεξτε χρωμα")}
                </Typography>
              )}
              {customSurcharge > 0 && (
                <Typography variant="caption" sx={{ fontSize: "0.62rem", color: "#d97706", fontWeight: 700, mt: 0.25, display: "block" }}>
                  {t("Custom Size")}: +€{customSurcharge.toFixed(2)}
                </Typography>
              )}
              {selectedDoor && (
                <Typography variant="caption" sx={{ fontSize: "0.62rem", color: "#1d4ed8", fontWeight: 700, mt: 0.25, display: "block" }}>
                  {selectedDoor.option_name || selectedDoor.name}
                </Typography>
              )}
              {hingedEnabled && hingeSurcharge > 0 && (
                <Typography variant="caption" sx={{ fontSize: "0.62rem", color: "#7c3aed", fontWeight: 700, mt: 0.25, display: "block" }}>
                  {t("Κρεμαση")} ({hingeCm}cm): +€{hingeSurcharge.toFixed(0)}
                </Typography>
              )}
              {hingedEnabled && modelAtr1 == null && (
                <Typography variant="caption" sx={{ fontSize: "0.62rem", color: "#64748b", fontWeight: 600, mt: 0.25, display: "block" }}>
                  {t("Κρεμαση")}: {t("Χωρις διαφορα")}
                </Typography>
              )}
            </Box>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              {showAddQty && (
                <Stack direction="row" alignItems="center" spacing={0.25} sx={{ border: "1px solid #cbd5e1", borderRadius: 1, px: 0.5 }}>
                  <IconButton size="small" onClick={() => setAddQty(q => Math.max(1, q - 1))} sx={{ width: 22, height: 22, color: "#64748b" }}>
                    <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, lineHeight: 1 }}>−</Typography>
                  </IconButton>
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, minWidth: 20, textAlign: "center" }}>{addQty}</Typography>
                  <IconButton size="small" onClick={() => setAddQty(q => q + 1)} sx={{ width: 22, height: 22, color: "#64748b" }}>
                    <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, lineHeight: 1 }}>+</Typography>
                  </IconButton>
                </Stack>
              )}
              <Button
                variant="contained"
                size="small"
                startIcon={<AddShoppingCartIcon />}
                onClick={handleAdd}
                disabled={
                  (isCabinetMode && !activeSwatch)
                  || (customWEnabled && !customW)
                  || (customDEnabled && !customD)
                  || (customHEnabled && !customH)
                  || (isMeterUnit && (!meterInput || parseFloat(meterInput) <= 0))
                  || (isCubicUnit && (!cubicL || !cubicW || !cubicH || parseFloat(cubicL) <= 0 || parseFloat(cubicW) <= 0 || parseFloat(cubicH) <= 0))
                  || (doorOptions.length > 0 && !selectedDoor)
                }
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  bgcolor: "#3b82f6",
                  "&:hover": { bgcolor: "#2563eb" },
                }}
              >
                {t("Add")}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}
    </Box>
  )
}

export default ProductGrid
